<template>
  <!-- Simple Floating Widget -->
  <ClientOnly>
    <div
      v-if="surveyWidget.showWidget"
      class="fixed top-20 lg:top-28 mx-2 md:left-6 max-w-lg md:w-1/2 lg:w-1/3 xl:w-1/4 bg-white border border-gray-200 rounded-xl z-50 p-4 mt-2"
      style="
        box-shadow:
          0 8px 24px rgba(0, 0, 0, 0.15),
          0 -8px 24px rgba(0, 0, 0, 0.1);
      "
    >
      <p class="text-base font-medium text-gray-800 mb-3 leading-snug font-semibold">
        {{ $t("clientFeedBack.survey_widget_title") }}
      </p>
      <div class="flex gap-3 justify-center">
        <BaseButton
          @buttonClicked="surveyWidget.dismissTemporarily"
          class="mt-4 font-bold"
          :text="$t('common.not_now')"
          variant="outlined"
        ></BaseButton>
        <BaseButton @buttonClicked="openSurveyModal" class="mt-4 font-bold" :text="$t('common.sure')"></BaseButton>
      </div>
    </div>

    <template v-if="isModalOpen">
      <transition name="modalFade" appear>
        <div
          @click.self="closeModal"
          class="fixed flex items-center z-50 first-letter: h-screen w-screen bottom-0 top-0 left-0 right-0 bg-black/25"
        >
          <form
            v-if="step == 1"
            @submit.prevent="submitSurvey"
            class="w-[90%] lg:w-[55%] ml-auto mr-auto align-top flex flex-col bg-white border rounded-lg px-8 max-h-[85vh]"
          >
            <div class="overflow-y-auto">
              <h2 class="mt-8 text-primary text-center text-lg">
                {{ $t("clientFeedBack.survey_intro_text") }}
              </h2>
              <div
                v-for="question in surveyQuestions"
                :key="question.key"
                class="mt-8 flex flex-col md:flex-row md:justify-between md:items-center"
              >
                <label class="font-semibold text-base">{{ $t(question.label) }}</label>
                <div class="flex mt-2 md:mt-0 flex-shrink-0">
                  <label class="group" v-for="feedbackIcon in feedbackIcons" :key="feedbackIcon.rating">
                    <input
                      v-model="feedback[question.key]"
                      class="absolute hidden"
                      type="radio"
                      :value="feedbackIcon.rating"
                    />
                    <NuxtImg
                      :src="feedbackIcon.src"
                      :alt="feedbackIcon.alt"
                      class="mr-4 w-10 h-10 pt-1 hover:cursor-pointer"
                      :class="{
                        'rounded-full border-[#00A1AA] border-4 align-bottom border-rounded hover:cursor-not-allowed':
                          feedbackIcon.rating === feedback[question.key],
                      }"
                    />
                  </label>
                </div>
              </div>

              <div class="mt-8"></div>
              <div class="w-[100%]">
                <BaseTextArea
                  class="h-28 text-sm"
                  @input="updateComment"
                  name="comment"
                  :label="$t('clientFeedBack.leave_comment')"
                />
              </div>
            </div>
            <div class="mt-8 mb-8 flex justify-center flex-shrink-0">
              <BaseButton
                class="w-[155px] mr-12"
                @click="closeModal"
                :text="$t('common.btnClose')"
                variant="outlined"
              />

              <BaseButton
                class="pl-4 pr-4 w-[180px]"
                :text="$t('checkout.clientFeedBack.btnSendFeedback')"
                type="submit"
                :loading="pending"
                :disabled="surveyQuestions.some((q) => feedback[q.key] === 0)"
              />
            </div>
          </form>
          <div
            v-else-if="step == 2"
            class="w-[90%] h-fit lg:w-[40%] ml-auto mr-auto align-top flex flex-col bg-white border rounded-lg px-8"
          >
            <div class="text-center text-primary text-[17px] font-bold mt-12">
              <p>
                {{ $t("clientFeedBack.thank_you_message") }}
              </p>
            </div>
            <div class="mt-8 mb-8 flex justify-center">
              <BaseButton class="w-[155px]" @click="closeModal" :text="$t('common.finish')" variant="outlined" />
            </div>
          </div>
        </div>
      </transition>
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import { useSubmitFeedback } from "@/composables/useFeedbackForm";
import { useSurveyWidgetStore } from "@/stores/surveyWidget";
import { reactive, ref, watch } from "vue";
import { feedbackIcons } from "~/components/checkout/ClientFeedback/ClientFeedbackIcons";

interface FeedbackState {
  [key: string]: number | string;
  comment: string;
}

const surveyQuestions = [
  {
    key: "ease_of_finding",
    label: "clientFeedBack.ease_of_finding",
  },

  {
    key: "understanding_test_options_rating",
    label: "clientFeedBack.understanding_test_options",
  },

  {
    key: "site_performance_rating",
    label: "clientFeedBack.site_performance",
  },

  {
    key: "rating",
    label: "clientFeedBack.website_experience",
  },
];

const isModalOpen = ref(false);
const step = ref(1);
const feedbackId = ref(0);

const initialFeedbackState = surveyQuestions.reduce(
  (acc, question) => {
    acc[question.key] = 0;
    return acc;
  },
  { comment: "" } as FeedbackState
);

const feedback = reactive(initialFeedbackState);

const pending = ref(false);

const updateComment = (event: Event) => {
  feedback.comment = (event.target as HTMLInputElement).value;
};

const submitSurvey = async () => {
  const { rating, comment, ...meta } = feedback;
  const initialPayload = { rating, comment };
  const metaPayload = { meta };

  try {
    pending.value = true;
    const { data } = await useSubmitFeedback(initialPayload);
    feedbackId.value = data.value.data.feedback.id;
    if (feedbackId.value) {
      await useUpdateFeedback(feedbackId.value, metaPayload);
    }
    pending.value = false;
  } catch (error: any) {
    pending.value = false;
    console.error("❌ API error message:", error?.data?.message);
  }

  step.value = 2;
  surveyWidget.completeSurvey();
};

const route = useRoute();
const { locale } = useI18n();

//Utility to strip locale prefix from current path
//e.g /en-au/get-started -> /get-started
//e.g /en-au -> /
function getNormalizedPath(r: any) {
  const localeCode = locale.value; // e.g. "en-au"
  const prefixRegex = new RegExp(`^/${localeCode}(?=/|$)`);
  return r.path.replace(prefixRegex, "") || "/";
}

const surveyWidget = useSurveyWidgetStore();

// Initial check
surveyWidget.checkVisibility(getNormalizedPath(route), locale.value);

// Watch for route changes (path only — query/hash changes must not
// restart the countdown; query-driven suppression lives in the store)
watch(
  () => route.path,
  () => {
    const normalizedPath = getNormalizedPath(route);
    surveyWidget.checkVisibility(normalizedPath, locale.value);
  }
);

// open floating survey form modal
const openSurveyModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};
</script>

<style scoped>
:deep(.base-textarea) {
  height: 7rem; /* 112px, corresponds to h-28 */
  font-size: 0.875rem; /* 14px, corresponds to text-sm */
  line-height: 1.25rem;
}
</style>
